import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'

import { useWindowDimensions } from 'utils/hooks/useWindowDimensions'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const INITIAL_SCALE = 1.0
const PADDING_SIZE = 32

export const PDFReader = ({ url, isSticky = false }) => {
  const [wrapperWidth, setWrapperWidth] = useState<number>(0)
  const { width, getElementDimensionById } = useWindowDimensions()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(INITIAL_SCALE)
  const [isLoaded, setIsLoaded] = useState(false)
  const file = useMemo(() => ({ url }), [url])
  const rootRef = React.useRef<HTMLDivElement>(null)

  const onDocumentLoadSuccess = ({ numPages }) => {
    setIsLoaded(true)
    setNumPages(numPages)
  }

  useEffect(() => {
    const { width: elementWidth } = getElementDimensionById('viewerWrapper')
    setWrapperWidth(elementWidth)
  }, [getElementDimensionById])

  return (
    <>
      <div
        ref={rootRef}
        className="relative border border-gray-5 rounded-sm px-4 py-2 bg-gray-5"
      >
        {isLoaded && (
          <div
            className={clsx(
              'z-10',
              isSticky ? 'fixed top-[72px]' : 'absolute top-4'
            )}
            style={{
              right: isSticky
                ? (window.innerWidth - rootRef.current?.offsetWidth) / 2
                : 0,
            }}
          >
            <button
              data-testid="zoom_in"
              onClick={() => setScale((old) => old + 0.3)}
              className="flex items-center justify-center w-8 h-8 bg-white shadow-sm border border-gray-50 rounded-t"
            >
              <Icon>zoom_in</Icon>
            </button>
            <button
              data-testid="zoom_out"
              onClick={() =>
                setScale((old) => (old - 0.3 >= 0.1 ? old - 0.3 : 0.1))
              }
              className="flex items-center justify-center w-8 h-8 bg-white shadow-sm border border-gray-50 border-t-0 rounded-b"
            >
              <Icon>zoom_out</Icon>
            </button>
          </div>
        )}
        <div id="viewerContainer" className="relative z-0 overflow-auto">
          <div id="viewerWrapper">
            <Document
              file={file}
              error={() => (
                <Text
                  as="div"
                  className="w-full h-full flex items-center"
                  variant="body"
                  color="critical"
                >
                  <Icon color="critical">error_outline</Icon>
                  <span className="ml-1">
                    Terjadi kesalahan saat memuat dokumen...
                  </span>
                </Text>
              )}
              loading={() => (
                <Text
                  as="div"
                  className="w-full h-full flex items-center p-3 my-2 bg-surface-subdued border border-surface-neutral-subdued rounded"
                  variant="body"
                  color="informational"
                >
                  <Icon className="animate-spin" color="informational">
                    loop
                  </Icon>
                  <span className="ml-1">Sedang mengunduh dokumen...</span>
                </Text>
              )}
              externalLinkTarget="_blank"
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {[...Array(numPages)].map((_, index) => (
                <Page
                  key={`pdf-viewer-page-${index}@${scale}`}
                  pageNumber={index + 1}
                  className="pdf-viewer-page"
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  scale={scale}
                  width={(wrapperWidth || width) - PADDING_SIZE}
                />
              ))}
            </Document>
          </div>
        </div>
      </div>
    </>
  )
}

export default PDFReader
